import React, { memo } from "react";
import {
  Typography,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import PleaseNote from "../PleaseNote";
import { Image, Transformation } from "cloudinary-react";
import { fin, options, finAlum, finAlumBifold } from "./defaults";
import Video from "../Video";
import { RowLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useFinSelector from "./useFinSelector";
import { useIsReadOnlyContextProviderContext } from "../../context/IsReadOnlyContext";

const RowLayoutLeftDivide = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  borderLeft: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const RowLayoutSpace = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  padding: theme.spacing(1, 2),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  minWidth: 150,
  height: 20,
}));

const StyledDivRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
});

function FinAccordian() {
  const {
    handleClickFin,
    handleClickOptions,
    showSetbackInput,
    handleChange,
    isCentor,
    finChoice,
    finConfigChoice,
    finSetback,
    isAluminum,
    isBifoldAlum,
  } = useFinSelector();
  const { readOnly } = useIsReadOnlyContextProviderContext();

  return (
    <ReusableAccordian
      accordionName="fin"
      heading="Flashing Fin"
      secondaryHeading="Add flashing fin?"
      colOrRow="col"
    >
      <PleaseNote notes="Flashing fins are reccommended for proper installation" />
      {isBifoldAlum && isCentor && (
        <Typography variant="subtitle2">
          * Flashing Fin required for Bi-fold All Aluminum units with Centor screens
        </Typography>
      )}
      <StyledDivRow>
        <RowLayoutSpace>
          {isBifoldAlum ? (
            <div onClick={() => isCentor || readOnly ? null : handleClickFin(finAlumBifold.label)}>
              <Choices
                label={finAlumBifold.label}
                disabled={isCentor || readOnly}
                variant={
                  finAlumBifold.label === finChoice ? "filled" : "outlined"
                }
                checkMark={finAlumBifold.label === finChoice}
              />
            </div>
          ) : isAluminum ? (
            <div onClick={() => readOnly ? null : handleClickFin(finAlum.label)}>
              <Choices
                label={finAlum.label}
                disabled={readOnly}
                variant={finAlum.label === finChoice ? "filled" : "outlined"}
                checkMark={finAlum.label === finChoice}
              />
            </div>
          ) : (
            <div onClick={() => readOnly ? null : handleClickFin(fin.label)}>
              <Choices
                label={
                  isCentor ? fin.label.replace("(A)", "(C & D)") : fin.label
                }
                disabled={readOnly}
                variant={fin.label === finChoice ? "filled" : "outlined"}
                checkMark={fin.label === finChoice}
              />
            </div>
          )}
        </RowLayoutSpace>
        {finChoice && (
          <>
            <RowLayoutLeftDivide>
              {isAluminum
                ? options
                    .filter((itm) => itm.label !== "Custom Setback")
                    .map((state) => (
                      <div key={state.id}>
                        <Choices
                          label={state.label}
                          disabled={true}
                          variant="filled"
                          checkMark={true}
                        />
                      </div>
                    ))
                : options.map((state) => (
                    <div
                      key={state.id}
                      onClick={() => readOnly ? null : handleClickOptions(state.label)}
                    >
                      <Choices
                        label={state.label}
                        disabled={readOnly}
                        variant={
                          state.label === finConfigChoice
                            ? "filled"
                            : "outlined"
                        }
                        checkMark={state.label === finConfigChoice}
                      />
                    </div>
                  ))}
            </RowLayoutLeftDivide>

            {showSetbackInput && (
              <RowLayout>
                <StyledFormControl size="small">
                  <InputLabel>Setback</InputLabel>
                  <Select
                    value={finSetback}
                    onChange={handleChange}
                    name="fractionValue"
                    label="Setback"
                    disabled={readOnly}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={"1/4"}>1/4</MenuItem>
                    <MenuItem value={"5/16"}>5/16</MenuItem>
                    <MenuItem value={"3/8"}>3/8</MenuItem>
                    <MenuItem value={"7/16"}>7/16</MenuItem>
                    <MenuItem value={"1/2"}>1/2</MenuItem>
                    <MenuItem value={"9/16"}>9/16</MenuItem>
                    <MenuItem value={"5/8"}>5/8</MenuItem>
                    <MenuItem value={"11/16"}>11/16</MenuItem>
                    <MenuItem value={"3/4"}>3/4</MenuItem>
                    <MenuItem value={"13/16"}>13/16</MenuItem>
                    <MenuItem value={"7/8"}>7/8</MenuItem>
                    <MenuItem value={"15/16"}>15/16</MenuItem>
                    <MenuItem value={"1"}>1</MenuItem>
                    <MenuItem value={"1 1/16"}>1-1/16</MenuItem>
                    <MenuItem value={"1 1/8"}>1-1/8</MenuItem>
                    <MenuItem value={"1 3/16"}>1-3/16</MenuItem>
                    <MenuItem value={"1 1/4"}>1-1/4</MenuItem>
                    <MenuItem value={"1 5/16"}>1-5/16</MenuItem>
                    <MenuItem value={"1 3/8"}>1-3/8</MenuItem>
                    <MenuItem value={"1 7/16"}>1-7/16</MenuItem>
                    <MenuItem value={"1 1/2"}>1-1/2</MenuItem>
                  </Select>
                </StyledFormControl>
              </RowLayout>
            )}
          </>
        )}
      </StyledDivRow>

      {finChoice && isAluminum && !isBifoldAlum && !isCentor && (
        <FinImage publicId="FlashingFin/alum_fin" />
      )}

      {finChoice && isBifoldAlum && (
        <FinImage publicId="FlashingFin/alum_fin_bifold" />
      )}

      {finChoice && !isCentor && !isAluminum && (
        <FinImage publicId="v1670437009/FlashingFin/fin_1inch_setback" />
      )}

      {finChoice && isCentor && !isAluminum && (
        <FinImage publicId="v1670437009/FlashingFin/fin_centor" />
      )}

      {finChoice && isBifoldAlum ? null : (
        <Video src="https://www.youtube.com/watch?v=qFQ_DusKIP8" />
      )}
    </ReusableAccordian>
  );
}

interface FinImageProps {
  publicId: string;
}

const FinImage: React.FC<FinImageProps> = ({ publicId }) => {
  return (
    <RowLayout>
      <Image cloudName="ag-millworks" secure="true" publicId={publicId}>
        <Transformation height="250" width="400" crop="scale" />
      </Image>
    </RowLayout>
  );
};

export default memo(FinAccordian);
